/**
 * Appearance state shared across the pane: font size + dark/light mode.
 * Persisted to localStorage so preferences survive reloads.
 */

import * as React from 'react';

/* global localStorage */

export type FontSize = 'small' | 'medium' | 'large';

interface AppearanceState {
  fontSize: FontSize;
  isDark: boolean;
  setFontSize: (s: FontSize) => void;
  toggleDark: () => void;
  setIsDark: (v: boolean) => void;
}

const DARK_KEY = 'signus.isDark';

/**
 * Font size always defaults to "medium" on load. We deliberately do not persist
 * across sessions — users who want a different size pick it per session.
 * This avoids the surprise where a previous tester's preference sticks around.
 */
export function loadFontSize(): FontSize {
  return 'medium';
}

export function loadIsDark(): boolean {
  try {
    return localStorage.getItem(DARK_KEY) === '1';
  } catch {
    return false;
  }
}

export const AppearanceContext = React.createContext<AppearanceState>({
  fontSize: 'medium',
  isDark: false,
  setFontSize: () => {},
  toggleDark: () => {},
  setIsDark: () => {},
});

export function useAppearance(): AppearanceState {
  return React.useContext(AppearanceContext);
}

export function persistFontSize(_s: FontSize): void {
  // Intentionally a no-op — font size resets to medium on every session.
}

export function persistIsDark(v: boolean): void {
  try {
    localStorage.setItem(DARK_KEY, v ? '1' : '0');
  } catch {
    /* noop */
  }
}
